import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedMode } from "../redux/modelSlice";
import api from "../api";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";

const ModeSelector = () => {
  const dispatch = useDispatch();
  const selectedMode = useSelector((state) => state.model.selectedMode);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // ✅ Fetch user preferences on mount
  useEffect(() => {
    const fetchUserMode = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.mode_type) {
          dispatch(setSelectedMode(res.data.mode_type));
        }
      } catch (error) {
        console.error("❌ Failed to load user mode:", error);
      }
    };
    if (token) fetchUserMode();
  }, [dispatch, token]);

  // ✅ Unified function to call backend
  const updateUserPreferences = async (mode) => {
    try {
      // Determine backend-safe values
      const payload = {
        privacy_mode: isFullPrivacy, 
        mode_type: mode, // global/personal/incognito
      };

      const res = await api.put(
        `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200) {
        showSuccessToast(`Mode changed to ${mode}.`);
      }
    } catch (error) {
      console.error("❌ Failed to update user mode:", error);
      // showErrorToast("Failed to update mode. Please try again.");
    }
  };

  // ✅ Handle dropdown mode change
  const handleModeChange = (mode) => {
    dispatch(setSelectedMode(mode)); // instant UI update
    setDropdownOpen(false);
    updateUserPreferences(mode); // backend sync
  };

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Mode → icon mapping
  const modeIcons = {
    global: "bi-globe2",
    personal: "bi-person-circle",
    incognito: "bi-incognito",
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      {/* Current Mode Icon */}
      <div
        className="mode-icon d-flex align-items-center justify-content-center"
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#F0F0F0",
          fontSize: "18px",
          cursor: "pointer",
        }}
        title={selectedMode?.charAt(0).toUpperCase() + selectedMode?.slice(1)}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <i className={`bi ${modeIcons[selectedMode] || "bi-question-circle"}`}></i>
      </div>

      {/* Dropdown */}
      {dropdownOpen && (
        <div
          className="dropdown-menu show profile-dropdown"
          style={{ minWidth: "160px", marginTop: "-9px" }}
        >
          {["global", "personal"].map((mode) => (
            <button
              key={mode}
              className={`dropdown-item profile-dropdown-item d-flex align-items-center gap-3 ${
                selectedMode === mode ? "active" : ""
              }`}
              onClick={() => handleModeChange(mode)}
            >
              <i className={`bi ${modeIcons[mode]}`}></i>
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModeSelector;
